import { Component, OnInit, OnDestroy, Inject, LOCALE_ID, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormArray, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  AllowedBranches,
  AllowedNonCorps,
  ArbCentralNavSecurity,
  Customer,
  CustomerDetails,
  LookupLists,
  ManagedUserFilterLists,
  NonCorpStore,
  PartnerProgrammeCodes,
  State,
} from '@pos-app/data';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { ManageusersPartialState } from '../manageusers/+state/manageusers.reducer';
import { Store, select } from '@ngrx/store';
import { manageusersQuery } from '../manageusers/+state/manageusers.selectors';
import {
  LoadLookupLists,
  SavedSelectedCustomer,
  SavedTempSelectedCustomer,
  UpdateAccountCustomerSettings,
} from '../manageusers/+state/manageusers.actions';
import {
  ApiService,
  CoreUiPartialState,
  isExternalUser,
  hasUnsavedChanges,
  MessageService,
  getDefaultCustomer,
  DialogService,
  clearUnsavedChanges,
  getArbCentralNavSecurity,
} from '@pos-app/core-ui';
import { takeUntil, switchMap, take, map, delay, mergeMap, distinctUntilChanged } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { NgbDateStruct, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { OrdersPartialState } from '../orders/+state/orders.reducer';
import { LoadParkedOrdersList } from '../orders/+state/orders.actions';
import moment from 'moment';
import { CreateShipToCustomer, SelectCountry, UpdateShipToCustomer } from '../customers/+state/customers.actions';
import { customersQuery } from '../customers/+state/customers.selectors';
import { Router } from '@angular/router';
import { ExternalUser } from '../manageusers/+state/user.model';
import { R } from '@angular/cdk/keycodes';
import { CoreUiModule } from '../../../../../../libs/core-ui/src/lib/core-ui.module';
import { NgIf, NgFor, NgClass, AsyncPipe } from '@angular/common';
import { NgSelectComponent, NgOptionComponent } from '@ng-select/ng-select';
import { MatIcon } from '@angular/material/icon';
import { CustomersShipToFormComponent } from '../customers/components/customers-ship-to-form/customers-ship-to-form.component';

@Component({
    selector: 'app-manage-partner',
    templateUrl: './manage-partner.component.html',
    styleUrls: ['./manage-partner.component.scss'],
    imports: [CoreUiModule, NgIf, FormsModule, ReactiveFormsModule, NgSelectComponent, NgFor, NgOptionComponent, NgClass, MatIcon, CustomersShipToFormComponent, AsyncPipe]
})
export class ManagePartnerComponent implements OnInit, OnDestroy {
  @ViewChild('content') modalBox: any;
  selectedCustomer: Customer;
  selectedShipToCustomerNumber: string;
  securityFunctionList$: Observable<string[]>;
  preSelectedCustomerNumber = false;
  globalUserSecurityForm: UntypedFormGroup;
  lookupLists$: Observable<ManagedUserFilterLists>;
  externalUserList: ExternalUser[];

  isBusy$: Observable<boolean>;
  selectedCustomerName: string;
  isARBuAvailable: boolean = false;
  private unSubscribe$ = new Subject<void>();
  showResultUserSearchTable: boolean = false;
  filteredUser: any[];
  loading: Observable<boolean>;
  selectedUserEmail: string = '';
  showDefaultShipToActionTF: boolean = true;
  public formValue = null;
  public formValid = false;
  public formDirty = false;
  public selectedCustomerDetails$: Observable<CustomerDetails>;
  public filteredStateList: State[];
  isGarage = false;
  nonCorpStores: NonCorpStore[] = [];
  allCombinedBranchesList: any = [];
  initialFormValue: any;
  branchesList: any = [];
  nonCorpStoresList: any = [];
  priceScaleCodeList: any;
  isEditShipTo: boolean;
  editShipToCustomer: CustomerDetails;
  public selectedCustomerDetails: CustomerDetails;
  arbCentralSecurityFunctions: ArbCentralNavSecurity;
  hasManageProgrammePermission: boolean;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private store: Store<ManageusersPartialState>,
    private orderStore: Store<OrdersPartialState>,
    private apiService: ApiService,
    private toastr: ToastrService,
    private coreUIStore: Store<CoreUiPartialState>,
    private messageService: MessageService,
    private modalService: NgbModal,
    private router: Router,
    private dialogService: DialogService,
    @Inject(LOCALE_ID) public locale,
  ) {
    // Initialize the form group with default values and validators
    this.globalUserSecurityForm = this.formBuilder.group({
      branch: [''],
      inventoryView: ['', Validators.required],
      customerNumber: [''],
      currencyCode: [''],
      validOrderTypes: [''],
      displayCurrencyYN: [false],
      displayIncTaxYN: [false],
      displayExTaxYN: [false],
      showNationalInventoryYN: [false],
      ARBuAvailableYN: [false],
      allowedBranches: ['', Validators.required],
      allowedNonCorps: [''],
      partnerProgrammeCodes: [''],
      saveQuotesTF: [false],
      defaultXRefPartsTF: [false],
      shipTos: this.formBuilder.array([]),
      SeeBuyPrice: [false],
      MaintainOrders: [false],
      AccountScreen: [false],
      SeeCostMaster: [false],
      ViewStock: [false],
      ManageUsers: [false],
      JobRequests: [false], // Added JobRequests security function
      genericQuotesOnlyTF: [false],
      customPricingFunction: [''],
      allowProgrammeLoginOnlyTF: [false],
      overrideShowroomPriceCode: [''],
    });
  }

  ngOnInit() {
    // Fetch non-corporate stores
    this.apiService
      .getNonCorpStoresForShow()
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((storeData) => {
        this.nonCorpStores = storeData.SearchResults || [];
        this.nonCorpStoresList = storeData.SearchResults.map((branch: any) => {
          return {
            branchCode: branch.customerNumber?.toString().trim(),
            branchName: branch.customerName,
          };
        });
      });
    //fetch price code list from PriceScales
    this.apiService
      .getPriceScaleCodeList()
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((priceCodes) => {
        this.priceScaleCodeList = priceCodes.SearchResults || [];
      });

    // Dispatch actions to load lookup lists and parked orders
    this.store.dispatch(new LoadLookupLists());
    this.dispatchParkedOrdersList();

    // Select lookup lists and busy state from the store
    this.lookupLists$ = this.store.select(manageusersQuery.getLookupLists);
    this.isBusy$ = this.store.select(manageusersQuery.getIsBusy);

    this.lookupLists$.pipe(takeUntil(this.unSubscribe$)).subscribe((lookupLists) => {
      if (lookupLists) {
        this.branchesList = lookupLists?.branchList?.map((branch) => {
          return {
            branchCode: branch.BranchCode.trim(),
            branchName: branch.BranchDescription,
          };
        });
      }
    });

    // Combine observables to get default customer and external user
    combineLatest([this.coreUIStore.pipe(select(getDefaultCustomer)), this.coreUIStore.pipe(select(isExternalUser))])
      .pipe(
        take(1),
        switchMap(([customer, externalUser]) => {
          if (externalUser && customer) {
            this.preSelectedCustomerNumber = true;
            Object.keys(this.globalUserSecurityForm.controls).forEach((key) => {
              this.globalUserSecurityForm.controls[key].disable();
            });

            return this.apiService
              .customerSearch({
                customerfuzzySearch: '',
                customerNumber: +customer.customerNumber,
                customerType: 'billto',
              })
              .pipe(map((result) => result?.SearchResults?.find((item) => item.customerNumber === customer.customerNumber)));
          }
          return of(null);
        }),
      )
      .subscribe((result) => {
        if (result) {
          this.selectCustomer(result);
        }
      });

    // Select security function list from the store
    this.securityFunctionList$ = this.store.pipe(select(manageusersQuery.getSecurityFunctionList));

    // Fetch external user list
    this.apiService
      .getExternalUserList({ activeOnlyYN: 'Y', customerOnlyYN: 'Y' })
      .pipe()
      .subscribe((res) => {
        this.externalUserList = res.SearchResults;
      });

    this.globalUserSecurityForm.valueChanges.pipe(distinctUntilChanged(), takeUntil(this.unSubscribe$)).subscribe((val) => {
      this.compareValues();
    });
    this.globalUserSecurityForm
      .get('allowedBranches')
      .valueChanges.pipe(distinctUntilChanged(), takeUntil(this.unSubscribe$))
      .subscribe((val) => {
        if (val && val.length > 0) {
          this.globalUserSecurityForm.patchValue({ branch: this.globalUserSecurityForm.controls.allowedBranches?.value[0] });
        }
      });

    this.coreUIStore
      .select(getArbCentralNavSecurity)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((securityFunctions: ArbCentralNavSecurity) => {
        if (securityFunctions) {
          this.hasManageProgrammePermission = securityFunctions.manageProgrammeActionTF || false;
        }
      });

    this.store
      .select(manageusersQuery.getTempSelectedCustomer)
      .pipe(take(1)) // Only take the first value and complete
      .subscribe((customer) => {
        if (customer) {
          // Map CustomerDetails -> Customer filling missing fields with sensible defaults
          this.selectedCustomer = customer;
          this.patchSelectedCustomerDetailsToForm();
          // Clear immediately after consuming it
          this.store.dispatch(new SavedTempSelectedCustomer(null));
        } else {
          // If no temp customer, clear any previously selected customer to start fresh
          this.store.dispatch(new SavedSelectedCustomer(null));
          this.selectedCustomer = null;
        }
      });

    this.formValid = false;
    this.store.dispatch(hasUnsavedChanges({ unsavedChanges: false }));
    this.messageService.dispatchAction(hasUnsavedChanges({ unsavedChanges: false }));
  }

  ngOnDestroy() {
    // Complete the unSubscribe$ subject to clean up subscriptions
    this.store.dispatch(new SavedSelectedCustomer(null));
    // Don't clear SavedTempSelectedCustomer here - it needs to survive navigation
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }

  selectCustomer(e) {
    this.reset();
    this.selectedCustomer = e;
    if (this.selectedCustomer) {
      this.hasARBuAvailable(this.selectedCustomer.ARBuAvailableYN === 'Y');
      if (this.selectedCustomer.allowedBranches || this.selectedCustomer.allowedNonCorps) {
        this.allCombinedBranchesList = this.allCombinedBranchesList.concat(
          this.selectedCustomer.allowedBranches?.map((branch) => {
            return {
              branchCode: branch?.branchCode.trim(),
              branchName: branch?.branchName,
            };
          }),
        );
        this.allCombinedBranchesList = this.allCombinedBranchesList.concat(
          this.selectedCustomer.allowedNonCorps.map((branch: any) => {
            return {
              branchCode: branch?.partnerCode.toString(),
              branchName: branch?.partnerName,
            };
          }),
        );
      }
      this.patchSelectedCustomerDetailsToForm();
      this.initialFormValue = this.globalUserSecurityForm.getRawValue();
      this.compareValues();
    }
  }

  createShipToGroup(shipTo): UntypedFormGroup {
    return this.formBuilder.group({
      shipToNumber: [shipTo.shipToNumber],
      shipToName: [shipTo.shipToName],
      defaultBranch: [shipTo.defaultBranch, Validators.required],
      corpBranchTF: [shipTo.corpBranchTF],
    });
  }

  displayDate(dateStr) {
    return moment(dateStr).format('L');
  }

  checkValidDate(dateEffective: NgbDateStruct, dateExpiry: NgbDateStruct) {
    const validExpiry = new Date(dateExpiry.year, dateExpiry.month - 1, dateExpiry.day) > new Date();
    if (!validExpiry) {
      this.toastr.error('Invalid expiry date');
      return false;
    }
    const validSetDate =
      new Date(dateExpiry.year, dateExpiry.month - 1, dateExpiry.day) > new Date(dateEffective.year, dateEffective.month - 1, dateEffective.day);

    if (!validSetDate) {
      this.toastr.error('Effective date must be before expiry date');
      return false;
    }
    return true;
  }

  updateGlobalSetting() {
    if (!this.globalUserSecurityForm.controls.branch.value) {
      this.globalUserSecurityForm.patchValue({ branch: '' });
    }
    if (!this.globalUserSecurityForm.controls.validOrderTypes.value) {
      this.globalUserSecurityForm.patchValue({ validOrderTypes: [] });
    }

    this.shipTos.controls.forEach((control, i) => {
      if (control.get('defaultBranch').value) {
        //check if shiptos.defaultBranch value form branchList or nonCorpBranchList
        if (this.branchesList.some((el) => el?.branchCode === control?.get('defaultBranch').value)) {
          this.shipTos.controls[i]?.patchValue({ corpBranchTF: true });
        } else {
          this.shipTos.controls[i]?.patchValue({ corpBranchTF: false });
        }
      }
    });

    // securityFunctionAllowed = [{securityFunction: 'SeeBuyPrice', allowedTF: false}, {securityFunction: 'MaintainOrders', allowedTF: false}, {securityFunction: 'AccountScreen', allowedTF: false}, {securityFunction: 'SeeCostMaster', allowedTF: false}, {securityFunction: 'ViewStock', allowedTF: false}, {securityFunction: 'ManageUsers', allowedTF: false}] with the form values
    const securityFunctionAllowed = Object.keys(this.globalUserSecurityForm.controls)
      .filter((key) => {
        return (
          key === 'SeeBuyPrice' ||
          key === 'MaintainOrders' ||
          key === 'AccountScreen' ||
          key === 'SeeCostMaster' ||
          key === 'ViewStock' ||
          key === 'ManageUsers' ||
          key === 'JobRequests' // Include JobRequests in the security functions
        );
      })
      .map((key) => {
        return { securityFunction: key, allowedTF: this.globalUserSecurityForm.controls[key].value };
      });
    //filter SeeBuyPrice, MaintainOrders, AccountScreen, SeeCostMaster, SeeCostMaster, ViewStock, ManageUsers field and value from globalUserSecurityForm
    Object.keys(this.globalUserSecurityForm.controls)
      .filter((key) => {
        return (
          key === 'SeeBuyPrice' ||
          key === 'MaintainOrders' ||
          key === 'AccountScreen' ||
          key === 'SeeCostMaster' ||
          key === 'ViewStock' ||
          key === 'ManageUsers' ||
          key === 'JobRequests' // Include JobRequests in the security functions
        );
      })
      .forEach((key) => {
        this.globalUserSecurityForm.removeControl(key);
      });

    this.store.dispatch(
      new UpdateAccountCustomerSettings({
        ...this.globalUserSecurityForm.value,
        displayCurrencyYN: this.globalUserSecurityForm.controls.displayCurrencyYN.value ? 'Y' : 'N',
        displayExTaxYN: this.globalUserSecurityForm.controls.displayExTaxYN.value ? 'Y' : 'N',
        displayIncTaxYN: this.globalUserSecurityForm.controls.displayIncTaxYN.value ? 'Y' : 'N',
        showNationalInventoryYN: this.globalUserSecurityForm.controls.showNationalInventoryYN.value ? 'Y' : 'N',
        ARBuAvailableYN: this.globalUserSecurityForm.controls.ARBuAvailableYN.value ? 'Y' : 'N',
        securityFunctionsAllowed: securityFunctionAllowed,
        overrideShowroomPriceCode: this.globalUserSecurityForm.controls.overrideShowroomPriceCode?.value?.toString().trim().toUpperCase(),
      }),
    );
    //add deleted control to globalUserSecurityForm
    this.globalUserSecurityForm.addControl('SeeBuyPrice', this.formBuilder.control(false));
    this.globalUserSecurityForm.addControl('MaintainOrders', this.formBuilder.control(false));
    this.globalUserSecurityForm.addControl('AccountScreen', this.formBuilder.control(false));
    this.globalUserSecurityForm.addControl('SeeCostMaster', this.formBuilder.control(false));
    this.globalUserSecurityForm.addControl('ViewStock', this.formBuilder.control(false));
    this.globalUserSecurityForm.addControl('ManageUsers', this.formBuilder.control(false));
    this.globalUserSecurityForm.addControl('JobRequests', this.formBuilder.control(false));

    this.store
      .select(manageusersQuery.getARBuAvailable)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((response) => {
        this.hasARBuAvailable(response);
      });

    this.reset();
  }

  onSelectUser(user: ExternalUser) {
    this.reset();
    this.selectedCustomer = null;
    this.selectedCustomerName = null;
    this.selectedUserEmail = user.email;

    if (user) {
      this.apiService
        .customerSearch({
          customerfuzzySearch: '',
          customerNumber: +user?.parentCustomerNumber,
          customerType: '',
        })
        .pipe(map((result) => result?.SearchResults?.find((item) => +item.customerNumber === +user?.parentCustomerNumber)))
        .subscribe((customer) => {
          if (customer) {
            this.selectCustomer(customer);
            this.selectedUserEmail = '';
          }
        });
    }
  }

  dispatchParkedOrdersList() {
    this.orderStore.dispatch(new LoadParkedOrdersList());
  }

  hasARBuAvailable(ARBuAvailableYN: boolean) {
    this.isARBuAvailable = ARBuAvailableYN;
  }

  isFocus(e) {
    this.reset();
    this.showResultUserSearchTable = true;
    e.target.select();
  }

  lostFocus(e) {
    setTimeout(() => {
      this.showResultUserSearchTable = false;
    }, 300);
  }

  searchUser(e: string) {
    this.loading = of(true);
    this.filteredUser = this.externalUserList.filter((user) => {
      return user.email.toLowerCase().includes(e.toLowerCase());
    });
    setTimeout(() => {
      this.loading = of(false);
    }, 200);
  }

  formDirtyHandler(dirty) {
    this.formDirty = dirty;
    if (dirty) {
      this.store.dispatch(hasUnsavedChanges({ unsavedChanges: true }));
      this.messageService.dispatchAction(hasUnsavedChanges({ unsavedChanges: true }));
    }
  }

  cancel() {
    this.editShipToCustomer = null;
    this.isEditShipTo = false;
    this.reset();
  }
  reset() {
    this.globalUserSecurityForm.setControl('shipTos', this.formBuilder.array([]));
    this.globalUserSecurityForm.reset();
    this.selectedCustomer = null;
    //dispatch clearUnsavedChanges action
    this.store.dispatch(hasUnsavedChanges({ unsavedChanges: false }));
    this.messageService.dispatchAction(hasUnsavedChanges({ unsavedChanges: false }));
  }

  getFilteredStateList(countryCode) {
    this.store.dispatch(new SelectCountry(countryCode));
    this.lookupLists$.pipe(takeUntil(this.unSubscribe$)).subscribe((lookupLists) => {
      if (lookupLists) {
        this.filteredStateList = lookupLists?.stateList?.filter((x) => x.CountryCode === countryCode);
      }
    });
  }

  openAddShipToModal() {
    this.isGarage = true;
    this.openModalBox();
    this.selectedCustomerDetails$ = of({
      customerName: this.selectedCustomer.customerName,
      customerNumber: this.selectedCustomer.customerNumber,
    } as CustomerDetails);
  }

  openEditShipToModal(customerNumber?: string, customerName?: string) {
    this.isGarage = false;
    this.isEditShipTo = true;

    this.apiService
      .fetchCustomerDetails({
        customerNumber: customerNumber,
      })
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((data) => {
        if (data) {
          this.selectedCustomerDetails = data.SearchResults[0];
          this.openModalBox();
          this.selectedCustomerDetails$ = of({
            customerName: this.selectedCustomer.customerName,
            customerNumber: this.selectedCustomer.customerNumber,
          } as CustomerDetails);
        }
      });
  }

  openModalBox() {
    this.modalService
      .open(this.modalBox, {
        windowClass: 'garage-modal',
        size: 'xl',
        backdrop: 'static',
      })
      .result.then(
        (_) => {
          this.isGarage = false;
          this.isEditShipTo = false;
        },
        (_) => {
          this.isGarage = false;
          this.isEditShipTo = false;
        },
      );
  }

  handleCloseModal(event) {
    if (event) {
      this.modalService.dismissAll();
    }
  }

  navigateTo(url, extras?: any, isSearch = false, isMyOrder = false) {
    this.router.navigateByUrl(url, extras);
  }

  get shipTos(): UntypedFormArray {
    return this.globalUserSecurityForm.get('shipTos') as UntypedFormArray;
  }

  removeShipTo(index: number) {
    this.shipTos.removeAt(index);
  }

  saveShipTo() {
    //if this.formValue is undefined set to object value to  "" , which mean addressLine2
    this.modalService.dismissAll();
    if (this.isEditShipTo) {
      this.loading = of(true);
      this.apiService
        .updateShipToCustomer(this.formValue)
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe((result) => {
          if (result.ErrorFlag === '0') {
            this.loading = of(false);
            this.toastr.success('Ship To customer updated successfully', 'Success');
            //refresh to get updated data

            this.apiService
              .fetchCustomerDetails({
                customerNumber: this.selectedCustomer.customerNumber,
              })
              .pipe(takeUntil(this.unSubscribe$))
              .subscribe((data) => {
                this.selectCustomer(data.SearchResults[0]);
              });
          } else {
            this.loading = of(false);
          }
        });
      return;
    }
    this.loading = of(true);
    this.apiService
      .createShipToCustomer(this.formValue)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((result) => {
        this.modalService.dismissAll();
        if (result.ErrorFlag === '0') {
          this.dialogService.confirm(
            `${this.formValue.name} (${result.CustomerNumber})`,
            'Ship To customer created successfully',
            null,
            null,
            null,
            null,
            false,
          );
          //refresh to get updated data
          this.apiService
            .fetchCustomerDetails({
              customerNumber: this.selectedCustomer.customerNumber,
            })
            .pipe(takeUntil(this.unSubscribe$))
            .subscribe((data) => {
              this.loading = of(false);

              this.selectCustomer(data.SearchResults[0]);
            });
        }
      });
  }

  compareValues() {
    const currentFormValue = this.globalUserSecurityForm.getRawValue();
    if (JSON.stringify(currentFormValue) !== JSON.stringify(this.initialFormValue)) {
      this.formValid = true;
      this.store.dispatch(hasUnsavedChanges({ unsavedChanges: true }));
      this.messageService.dispatchAction(hasUnsavedChanges({ unsavedChanges: true }));
    } else {
      this.formValid = false;
      this.store.dispatch(hasUnsavedChanges({ unsavedChanges: false }));
      this.messageService.dispatchAction(hasUnsavedChanges({ unsavedChanges: false }));
    }
    let availableBranchesList;
    let availableNonCorpBranchList;

    if (this.selectedCustomer) {
      if (this.globalUserSecurityForm.controls.allowedBranches?.value) {
        availableBranchesList = this.branchesList.filter((branch) =>
          this.globalUserSecurityForm.controls.allowedBranches?.value?.includes(branch.branchCode),
        );
      }
      if (this.globalUserSecurityForm.controls.allowedNonCorps?.value) {
        availableNonCorpBranchList = this.nonCorpStoresList.filter((branch) =>
          this.globalUserSecurityForm.controls.allowedNonCorps?.value?.includes(branch.branchCode),
        );
      }
      this.allCombinedBranchesList = availableBranchesList?.concat(availableNonCorpBranchList) ?? this.allCombinedBranchesList;
      //if shipToValue branchCode not includes in allCombinedBranchesList then return false
      this.shipTos.controls.forEach((control, i) => {
        if (this.isBranchInValid(control) || !control.get('defaultBranch').value) {
          this.shipTos.controls[i]?.get('defaultBranch').setErrors({ invalidBranch: true });
        } else {
          this.shipTos.controls[i]?.get('defaultBranch').setErrors(null);
        }
      });
    }
  }

  resetFormSelectedCustomer() {
    this.globalUserSecurityForm.setControl('shipTos', this.formBuilder.array([]));
    this.globalUserSecurityForm.reset();
    this.globalUserSecurityForm.patchValue({
      customer: this.selectedCustomer.customerName + '(' + this.selectedCustomer.customerNumber + ')',
      branch: this.selectedCustomer.branchCode,
      partnerProgrammeCodes: this.selectedCustomer.partnerProgrammeCodes.map((x: any) => x),
      allowedBranches: this.selectedCustomer.allowedBranches.map((x: any) => x.branchCode),
      allowedNonCorps: this.selectedCustomer.allowedNonCorps.map((x: any) => x.partnerCode.toString()),
      customerNumber: this.selectedCustomer.customerNumber,
      inventoryView: this.selectedCustomer.inventoryView,
      validOrderTypes: this.selectedCustomer.validOrderTypes.map((x: any) => x),
      currencyCode: this.selectedCustomer.currencyCode,
      displayCurrencyYN: this.selectedCustomer.displayCurrencyYN === 'Y',
      displayExTaxYN: this.selectedCustomer.displayExTaxYN === 'Y',
      displayIncTaxYN: this.selectedCustomer.displayIncTaxYN === 'Y',
      showNationalInventoryYN: this.selectedCustomer.showNationalInventoryYN === 'Y',
      ARBuAvailableYN: this.selectedCustomer.ARBuAvailableYN === 'Y',
      saveQuotesTF: this.selectedCustomer.saveQuotesTF,
      defaultXRefPartsTF: this.selectedCustomer.defaultXRefPartsTF,
      shipTos: this.selectedCustomer.shipTos?.map((x, i) => this.shipTos?.push(this.createShipToGroup(x))),
      genericQuotesOnlyTF: this.selectedCustomer.genericQuotesOnlyTF,
      overrideShowroomPriceCode: this.selectedCustomer.overrideShowroomPriceCode?.toUpperCase() || '',
    });
    this.formValid = false;
    this.store.dispatch(hasUnsavedChanges({ unsavedChanges: false }));
    this.messageService.dispatchAction(hasUnsavedChanges({ unsavedChanges: false }));
  }

  navigateToMaintainUser() {
    this.store.dispatch(new SavedTempSelectedCustomer(this.selectedCustomer));
    this.navigateTo('/pos/manageusers/external');
  }

  isBranchInValid(shipToValue) {
    //if shipToValue branchCode includes in allowedBranches or allowedNonCorps then return false
    if (this.selectedCustomer) {
      const isBranchValid = this.allCombinedBranchesList?.some((el) => el?.branchCode === shipToValue.value?.defaultBranch);
      return !isBranchValid;
    }
  }

  public updateShipToCustomer(): void {
    this.store.dispatch(new UpdateShipToCustomer(this.formValue));
  }

  loadShipToFromJDE() {
    if (this.selectedCustomer) {
      this.apiService
        .loadShipToFromJDE({ customerBillTo: this.selectedCustomer.customerNumber })
        .pipe(takeUntil(this.unSubscribe$))
        .subscribe((result) => {
          if (result.ErrorFlag === '0') {
            this.toastr.success(
              `<b>${result?.JDEShipTosFound}</b> - JDE ShipTo customers  found successfully</br>
              <b>${result?.partnerShipTosAdded}</b> - Partner ShipTo customers loaded ${result?.partnerShipTosAdded > 0 ? 'successfully' : ''}`,
              'Success',
              { enableHtml: true },
            );
            if (result?.JDEShipTosFound > 0 || result?.partnerShipTosAdded > 0) {
              this.resetFormSelectedCustomer();
              this.apiService
                .fetchCustomerDetails({
                  customerNumber: this.selectedCustomer.customerNumber,
                })
                .pipe(takeUntil(this.unSubscribe$))
                .subscribe((data) => {
                  if (data) {
                    this.selectCustomer(data.SearchResults[0]);
                  }
                });
            }
          } else {
            this.toastr.error('Failed to load Ship To customers', 'Error');
          }
        });
    } else {
      this.toastr.error('Please select a customer first', 'Error');
    }
  }

  patchSelectedCustomerDetailsToForm() {
    this.globalUserSecurityForm.patchValue({
      branch: this.selectedCustomer.branchCode,
      partnerProgrammeCodes: this.selectedCustomer.partnerProgrammeCodes.map((x: PartnerProgrammeCodes) => x),
      allowedBranches: this.selectedCustomer.allowedBranches?.map((x: AllowedBranches) => x.branchCode),
      allowedNonCorps: this.selectedCustomer.allowedNonCorps?.map((x: AllowedNonCorps) => x.partnerCode.toString()),
      customerNumber: this.selectedCustomer.customerNumber,
      inventoryView: this.selectedCustomer.inventoryView,
      validOrderTypes: this.selectedCustomer.validOrderTypes?.map((x: any) => x),
      currencyCode: this.selectedCustomer.currencyCode,
      displayCurrencyYN: this.selectedCustomer.displayCurrencyYN === 'Y',
      displayExTaxYN: this.selectedCustomer.displayExTaxYN === 'Y',
      displayIncTaxYN: this.selectedCustomer.displayIncTaxYN === 'Y',
      showNationalInventoryYN: this.selectedCustomer.showNationalInventoryYN === 'Y',
      ARBuAvailableYN: this.selectedCustomer.ARBuAvailableYN === 'Y',
      saveQuotesTF: this.selectedCustomer.saveQuotesTF,
      defaultXRefPartsTF: this.selectedCustomer.defaultXRefPartsTF,
      shipTos: this.selectedCustomer.shipTos?.map((x) => this.shipTos?.push(this.createShipToGroup(x))),
      genericQuotesOnlyTF: this.selectedCustomer.genericQuotesOnlyTF,
      customPricingFunction: this.selectedCustomer?.customPricingFunction ?? '',
      allowProgrammeLoginOnlyTF: this.selectedCustomer?.allowProgrammeLoginOnlyTF,
      overrideShowroomPriceCode: this.selectedCustomer?.overrideShowroomPriceCode,
    });
    this.selectedCustomer?.securityFunctionsAllowed?.forEach((securityFunction) => {
      const control = this.globalUserSecurityForm?.controls[securityFunction?.securityFunction];
      if (control) {
        control.patchValue(securityFunction?.allowedTF);
      }
    });
  }
}

